// frontend/src/contexts/OrganizationContext.jsx
import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const OrganizationContext = createContext();

export const useOrganization = () => {
  const context = useContext(OrganizationContext);
  if (!context) {
    throw new Error('useOrganization must be used within OrganizationProvider');
  }
  return context;
};

export const OrganizationProvider = ({ children }) => {
  const [organization, setOrganization] = useState(null);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Load organization from localStorage on mount
  useEffect(() => {
    const loadOrganization = () => {
      try {
        const userStr = localStorage.getItem('user');
        if (userStr) {
          const user = JSON.parse(userStr);
          if (user.organization) {
            setOrganization(user.organization);
            fetchOrganizationDetails();
          }
        }
      } catch (err) {
        console.error('Error loading organization:', err);
      } finally {
        setLoading(false);
      }
    };

    loadOrganization();
  }, []);

  // Fetch detailed organization info
  const fetchOrganizationDetails = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;

      const response = await axios.get(
        `${process.env.REACT_APP_API_URL || 'https://app.alpenscreen.ch/api'}/organizations/current`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      setOrganization(response.data);
      setError(null);
    } catch (err) {
      console.error('Error fetching organization details:', err);
      setError(err.message);
    }
  };

  // Fetch organization stats
  const fetchStats = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;

      const response = await axios.get(
        `${process.env.REACT_APP_API_URL || 'https://app.alpenscreen.ch/api'}/organizations/stats`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      setStats(response.data);
    } catch (err) {
      console.error('Error fetching organization stats:', err);
    }
  };

  // Update organization (name, logo, color)
  const updateOrganization = async (updates) => {
    try {
      const token = localStorage.getItem('token');
      if (!token) throw new Error('No token');

      const response = await axios.put(
        `${process.env.REACT_APP_API_URL || 'https://app.alpenscreen.ch/api'}/organizations/current`,
        updates,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      setOrganization(response.data);
      
      // Update localStorage
      const userStr = localStorage.getItem('user');
      if (userStr) {
        const user = JSON.parse(userStr);
        user.organization = response.data;
        localStorage.setItem('user', JSON.stringify(user));
      }

      return response.data;
    } catch (err) {
      console.error('Error updating organization:', err);
      throw err;
    }
  };

  // Apply branding (colors to CSS variables)
  useEffect(() => {
    if (organization?.color) {
      document.documentElement.style.setProperty('--primary-color', organization.color);
    }
  }, [organization?.color]);

  const value = {
    organization,
    stats,
    loading,
    error,
    fetchOrganizationDetails,
    fetchStats,
    updateOrganization,
    refreshOrganization: fetchOrganizationDetails,
    refreshStats: fetchStats
  };

  return (
    <OrganizationContext.Provider value={value}>
      {children}
    </OrganizationContext.Provider>
  );
};